///////////////////////////////////////////////////////////////////////////////
// RequestProduct — "Can't find what you need?" modal form.
//
// Entry points: carrier grid, gifts empty state, eSIM country picker, site
// footer, and 404 page all render <RequestProductLink> which opens the
// <RequestProductModal>. The modal POSTs to /api/product-requests with a
// Turnstile token.
///////////////////////////////////////////////////////////////////////////////
import React, { useState, useEffect, useRef } from 'react';
import { track } from './Chrome';

const MT = (typeof window !== 'undefined' && window.__MT) || {};

const TYPES = [
  { value: 'carrier', label: 'Mobile carrier / operator' },
  { value: 'country', label: 'Country' },
  { value: 'gift_card', label: 'Gift card / brand' },
  { value: 'esim', label: 'eSIM destination' },
  { value: 'other', label: 'Other' },
];

// views/web.pug only loads the Turnstile script on checkout/login/signup/
// contact, but this modal opens from the footer on every page — load it here.
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
function loadTurnstileScript() {
  if (window.turnstile || document.querySelector(`script[src="${TURNSTILE_SRC}"]`)) return;
  const el = document.createElement('script');
  el.src = TURNSTILE_SRC;
  el.async = true;
  el.defer = true;
  document.head.appendChild(el);
}

function useTurnstile(containerId) {
  const [token, setToken] = useState(null);
  const widgetIdRef = useRef(null);
  const rendered = useRef(false);

  useEffect(() => {
    const siteKey = MT.turnstileSiteKey;
    if (!siteKey || rendered.current) return;

    function render() {
      if (!window.turnstile || rendered.current) return;
      const el = document.getElementById(containerId);
      if (!el) return;
      rendered.current = true;
      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: siteKey,
        callback: (t) => setToken(t),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null),
        appearance: 'interaction-only',
      });
    }

    if (window.turnstile) { render(); return; }
    loadTurnstileScript();
    const iv = setInterval(() => { if (window.turnstile) { clearInterval(iv); render(); } }, 200);
    return () => clearInterval(iv);
  }, [containerId]);

  const reset = () => {
    setToken(null);
    if (widgetIdRef.current != null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  return { token, reset, pending: !!MT.turnstileSiteKey && !token };
}

// The link/button that opens the modal. Accepts optional defaults.
export function RequestProductLink({ label, defaultType, country, className }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={className || 'request-product-link'}
        onClick={() => { setOpen(true); track('product_request_open', { source: window.location.pathname }); }}
      >
        {label || "Can't find what you need? Request it"}
      </button>
      {open && (
        <RequestProductModal
          defaultType={defaultType}
          country={country}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function RequestProductModal({ defaultType, country, onClose }) {
  const [type, setType] = useState(defaultType || 'carrier');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const turnstile = useTurnstile('turnstile-product-request');
  const backdropRef = useRef(null);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    if (!name.trim()) { setError('Please describe what you\'re looking for.'); return; }
    if (turnstile.pending) { setError('Still running a quick security check. Try again in a moment.'); return; }
    setBusy(true);
    setError(null);

    try {
      const res = await fetch('/api/product-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          name: name.trim(),
          email: email.trim() || undefined,
          country: country || undefined,
          sourcePath: window.location.pathname,
          turnstileToken: turnstile.token,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
        track('product_request_submit', { type, country });
      } else {
        setError(data?.error?.message || 'Something went wrong. Please try again.');
        turnstile.reset();
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" ref={backdropRef}
      onClick={(e) => { if (e.target === backdropRef.current) onClose(); }}>
      <div className="modal request-modal">
        <div className="modal-head">
          <div className="modal-title">Request a product</div>
          <button type="button" className="modal-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body">
          {done ? (
            <div className="request-done">
              <p><b>Thank you!</b> We've noted your request. If you left your email, we'll let you know when it's available.</p>
              <button type="button" className="btn block" onClick={onClose}>Close</button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <p className="request-desc">Tell us what you'd like us to add — carrier, gift card, country, or eSIM destination.</p>

              <label className="request-label">
                Type
                <select value={type} onChange={(e) => setType(e.target.value)}>
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              </label>

              <label className="request-label">
                What are you looking for?
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Globe Philippines, Amazon UK, Vietnam eSIM"
                  maxLength={200} autoFocus
                />
              </label>

              <label className="request-label">
                Email <span className="optional">(optional — we'll notify you when it's added)</span>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </label>

              <div id="turnstile-product-request" />

              {error && <p className="request-error">{error}</p>}

              <button type="submit" className="btn block" disabled={busy}>
                {busy ? 'Sending…' : 'Submit request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
